import React, { useCallback, useEffect, useRef, useState } from 'react';
import Toolbar from 'src/components/Toolbar';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/Button';
import TextInput from 'src/components/TextInput';
import './style.scoped.css';
import { useHistory } from 'react-router-dom';
import feeSt, { FeeStore } from 'src/stores/FeeStore';
import { observer } from 'mobx-react-lite';
import ScrollItemSelector, { ScrollItem } from 'src/components/ScrollItemSelector';
import i18n from 'src/lang/i18n';
import calendaActiveIcon from 'src/assets/calendar_white.svg';
import calendaInactiveIcon from 'src/assets/calendar_blue.svg';
import hsycbhActiveIcon from 'src/assets/hsycbh_white.svg';
import hsycbhInActiveIcon from 'src/assets/hsycbh_blue.svg';
import dolarActiveIcon from 'src/assets/dollar_white.svg';
import dolarInactiveIcon from 'src/assets/dollar.svg';
import UserInfoService from 'src/utils/UserInfoService';
import policySt, { PolicyStore } from 'src/stores/PolicyStore';
import paymentSt, { PaymentStore } from 'src/stores/PaymentStore';
import { CONSTANT, FORMAT_DATE_YYYY_MM_DD } from 'src/constants';
import PopupService from 'src/utils/PopupService';
import InfoIcon from 'src/assets/info_icon.svg';
import { convertDateToDayValue, isValidDate, showContact } from 'src/utils';
import DatePicker from 'src/components/InputDatePicker';
import dayjs from 'dayjs';

const FeeMenuData: ScrollItem[] = [
  {
    id: 'periodic',
    title: i18n.t('periodic_payment'),
    iconActive: calendaActiveIcon,
    iconInactive: calendaInactiveIcon,
  },
  {
    id: 'hsycbh-fee',
    title: i18n.t('pay_fee_ycbh'),
    iconActive: hsycbhActiveIcon,
    iconInactive: hsycbhInActiveIcon,
  },
  {
    id: 'addition-fee',
    title: i18n.t('pay_fee_addition'),
    iconActive: dolarActiveIcon,
    iconInactive: dolarInactiveIcon,
  },
];

interface FeeFormObserverProps {
  feeStore?: FeeStore;
  policyStore?: PolicyStore;
  paymentStore?: PaymentStore;
}
const FeeFormObserver = observer(({ feeStore, policyStore, paymentStore }: FeeFormObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const scrollRef = useRef(null);
  const [contractCode, setContractCode] = useState<string>();
  const [errContractCode, setErrContractCode] = useState<string>();
  const [selectedItemScroll, setSelectedItemScroll]= useState<string>();
  const [checkRadio, setCheckRadio] = useState<number>(1);
  const [btnViewDetail, setBtnViewDetail] = useState<boolean>(false);
  const [periodicPolicyNumber, setPeriodicPolicyNumber] = useState<string>("");
  const [errPeriodicPolicyNumber, setErrPeriodicPolicyNumber] = useState<string>();
  const [topUpPolicyNumber, setTopUpPolicyNumber] = useState<string>("");
  const [errTopUpPolicyNumber, setErrTopUpPolicyNumber] = useState<string>();

  useEffect(() => {
    setSelectedItemScroll('hsycbh-fee');
    setCheckRadio(1);
  }, []);

  const [birthDate, setBirthDate] = useState();
  const [errBirthDate, setErrBirthDate] = useState(null);
  const [showingCalendar, setShowingCalendar] = useState(false);

  const _handleChangeDay = useCallback((date) => {
    setBirthDate(date);
    setErrBirthDate(null);
  }, []);

  const _handleFocusDatePicker = () => {
    setShowingCalendar(true);
  };

  const _handleBlurDatePicker = () => {
    setShowingCalendar(false);
  };

  const _handleSubmit = async () => {

    switch(selectedItemScroll){
      case "periodic": {
        if(checkRadio == 1) {

          if (!birthDate) {
            setErrBirthDate(t('dob_required'));
            return;
          }

          policyStore.getListPeriodicPolicyByIdCard({
            idType: UserInfoService.getUserInfo()?.idCardType ?? CONSTANT.ID_CARD_TYPE_DEFAULT,
            idNumber: UserInfoService.getUserInfo()?.idCardNo ?? CONSTANT.ID_CARD_NO_DEFAULT,
            history, birthDate: dayjs(birthDate).format(FORMAT_DATE_YYYY_MM_DD)
          });
        } else {
          if (!periodicPolicyNumber || !periodicPolicyNumber.trim()) {
            setErrPeriodicPolicyNumber(t('policy_number_required'));
          }else{
            paymentStore.getPeriodicPolicyPayment({
              policyNum: periodicPolicyNumber,
              bgIndex: null,
              history,
              showDetail: true
            });
          }
        }
        break;
      }
      case "hsycbh-fee": {
        if (!contractCode || !contractCode.trim()) {
          setErrContractCode(t('ycbh_number_required'));
        }else{
          feeStore.getInquiryHsycbh(contractCode, history);
        }
        break;
      }
      case "addition-fee": {
        if (!topUpPolicyNumber || !topUpPolicyNumber.trim()) {
          setErrTopUpPolicyNumber(t('policy_number_required'));
        }else{
          paymentStore.getTopUpPolicyPayment({
            policyNum: topUpPolicyNumber,
            bgIndex: null,
            history,
            showDetail: true
          }).catch(error => {
            let errorCode = error?.response?.data?.error_code;
            let errorMessage = error?.response?.data?.error_message;
            if(errorCode == '209'){
              PopupService.instance.current.open({
                visible: true,
                title: i18n.t('notification'),
                content: errorMessage,
                okTitle: i18n.t('no'),
                cancelTitle: i18n.t('periodic_payment_1'),
                onOk: () => {
                  PopupService.instance.current.close();
                },
                onCancel: () => {
                  PopupService.instance.current.close();
                  setSelectedItemScroll('periodic');
                  setCheckRadio(2);
                  setPeriodicPolicyNumber(topUpPolicyNumber);
                  scrollRef.current.scrollLeft = 0;
                },
                icon: InfoIcon
              });
            }else if(errorCode === '210'){
              showContact(errorMessage);
            }
          });
        }
        break;
      }
    }
  };

  const _handleChangeContractCode = useCallback((e) => {
    setContractCode(e.target.value.toUpperCase().slice(0, 20));
    setErrContractCode(null);
  }, []);

  const _handleChangePeriodicPolicyNumber = useCallback((e) => {
    setPeriodicPolicyNumber(e.target.value.toUpperCase());
    setErrPeriodicPolicyNumber(null);
  }, []);

  const _handleChangeTopUpPolicyNumber = useCallback((e) => {
    setTopUpPolicyNumber(e.target.value.toUpperCase());
    setErrTopUpPolicyNumber(null);
  }, []);

  const _handleBlurPeriodicPolicyNumber = useCallback((e) => {
    let value = e.target.value?.trim().toUpperCase() || "";
    setPeriodicPolicyNumber(value);
  }, []);

  const _handleBlurTopUpPolicyNumber = useCallback((e) => {
    let value = e.target.value?.trim().toUpperCase() || "";
    setTopUpPolicyNumber(value);
  }, []);

  const _handleClose = () => {
    history.push("/");
  };

  const _handleBack = () => {
    history.push("/");
  };

  const _handleSelectScrollMenu = (itemId) => {
    setSelectedItemScroll(itemId);
    setCheckRadio(1);
    if(itemId !== 'periodic'){
      setBtnViewDetail(true);
    }else {
      setBtnViewDetail(false);
    }
  };

  const _handleSelectTabPayFees = (param)=> {
    setCheckRadio(param);
    if(param == 2){
      setBtnViewDetail(true);
    }else {
      setBtnViewDetail(false);
    }
  }

  useEffect(() => {
    setBirthDate(undefined);
    setErrBirthDate(null);
  }, [selectedItemScroll, checkRadio]);

  return (
    <div className="screen-container bg-background">
      <Toolbar
        title={t('pay_fee_online')}
        type="normal"
        onClose={_handleClose}
        onBack={_handleBack}
        showClose={true}
        showBack={true}
      />

      <div>
        <div className="cardItemNoPadding pb-6">
          <div className="blockTitle blockTitleContainer">
            {t('chooser_type_pay_fee')}
          </div>
          <div className='px-4'>
            <ScrollItemSelector
              data={FeeMenuData}
              selected={selectedItemScroll}
              onSelect={_handleSelectScrollMenu}
              scrollRef={scrollRef}
            />
          </div>
        </div>

        {selectedItemScroll == 'periodic' &&
          <div className="view-periodic-payment">
            <div className="cardItemNoPadding">
              <div className="grid grid-cols-2 px-4 pb-4 pt-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="radio"
                    id="identification_id"
                    checked={checkRadio == 1}
                    onChange={()=>_handleSelectTabPayFees(1) }
                  />
                  <label
                    className="flex items-center cursor-pointer pl-2"
                    htmlFor="identification_id"

                  >
                    {' '}
                    {t('identification')}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="radio"
                    id="insuranceContract"
                    checked={checkRadio == 2}
                    onChange={()=>_handleSelectTabPayFees(2) }
                  />
                  <label
                    className="flex items-center cursor-pointer pl-2"
                    htmlFor="insuranceContract"
                  >
                    {' '}
                    {t('insurance_contract')}
                  </label>
                </div>
              </div>
              {checkRadio == 1 ? <>
                <div className="px-4 py-2">
                  <TextInput
                    label={t('identity_card_type')}
                    placeholder={''}
                    value={UserInfoService.getUserInfo()?.idCardType || t('identify_card_number_1')}
                    useDisabledStyle={true}
                    disabled={true}
                  />
                </div>
                <div className="px-4 py-2 pb-6">
                  <TextInput
                    label={t('identity_card_number')}
                    placeholder={''}
                    value={UserInfoService.getUserInfo()?.idCardNo || ''}
                    useDisabledStyle={true}
                    disabled={true}
                  />
                </div>
                <div className="px-4 py-2 pb-6">
                  <DatePicker
                    label={t('dob_full_ph')}
                    placeholder={t('date_format1')}
                    value={birthDate}
                    onChange={_handleChangeDay}
                    isRequired={true}
                    error={!!errBirthDate}
                    helperText={errBirthDate}
                    maxDate={new Date()}
                    onFocus={_handleFocusDatePicker}
                    onBlur={_handleBlurDatePicker}
                  />
                </div>
                </>
                  :
                <>
                  <div className="px-4 py-2 pb-6">
                    <TextInput
                      label={t('policy_number')}
                      placeholder={t('hint_insurance_policy_number')}
                      value={periodicPolicyNumber}
                      onChange={_handleChangePeriodicPolicyNumber}
                      error={!!errPeriodicPolicyNumber}
                      helperText={errPeriodicPolicyNumber}
                      onBlur={_handleBlurPeriodicPolicyNumber}
                    />
                  </div>
                  {/*<div className="px-4 py-2 pb-6">*/}
                  {/*  <DatePicker*/}
                  {/*    label={t('dob_full_ph')}*/}
                  {/*    placeholder={t('date_format1')}*/}
                  {/*    value={birthDate}*/}
                  {/*    onChange={_handleChangeDay}*/}
                  {/*    isRequired={true}*/}
                  {/*    error={!!errBirthDate}*/}
                  {/*    helperText={errBirthDate}*/}
                  {/*    maxDate={new Date()}*/}
                  {/*    onFocus={_handleFocusDatePicker}*/}
                  {/*    onBlur={_handleBlurDatePicker}*/}
                  {/*  />*/}
                  {/*</div>*/}
                </>
              }
            </div>
          </div>
        }

        {selectedItemScroll == 'hsycbh-fee' && <div className="cardItemNoPadding">
          <div className="px-4 pt-5 pb-6">
            <TextInput
              label={t('ycbh_number')}
              placeholder={t('ycbh_input_hint')}
              value={contractCode}
              onChange={_handleChangeContractCode}
              maxLength={20}
              error={!!errContractCode}
              helperText={errContractCode}
            />
          </div>
          {/*<div className="px-4 pt-5 pb-6">*/}
          {/*  <DatePicker*/}
          {/*    label={t('dob_full_ph')}*/}
          {/*    placeholder={t('date_format1')}*/}
          {/*    value={birthDate}*/}
          {/*    onChange={_handleChangeDay}*/}
          {/*    isRequired={true}*/}
          {/*    error={!!errBirthDate}*/}
          {/*    helperText={errBirthDate}*/}
          {/*    maxDate={new Date()}*/}
          {/*    onFocus={_handleFocusDatePicker}*/}
          {/*    onBlur={_handleBlurDatePicker}*/}
          {/*  />*/}
          {/*</div>*/}
        </div>}

        {selectedItemScroll == 'addition-fee' &&
          <div className="pay-extra-fee">
            <div className="cardItemNoPadding">
              <div className="px-4 py-2 pb-6 pt-5">
                  <TextInput
                    label={t('insurance_policy_number')}
                    placeholder={t('hint_insurance_policy_number')}
                    value={topUpPolicyNumber}
                    onChange={_handleChangeTopUpPolicyNumber}
                    error={!!errTopUpPolicyNumber}
                    helperText={errTopUpPolicyNumber}
                    onBlur={_handleBlurTopUpPolicyNumber}
                  />
              </div>
              {/*<div className="px-4 py-2 pb-6 pt-5">*/}
              {/*  <DatePicker*/}
              {/*    label={t('dob_full_ph')}*/}
              {/*    placeholder={t('date_format1')}*/}
              {/*    value={birthDate}*/}
              {/*    onChange={_handleChangeDay}*/}
              {/*    isRequired={true}*/}
              {/*    error={!!errBirthDate}*/}
              {/*    helperText={errBirthDate}*/}
              {/*    maxDate={new Date()}*/}
              {/*    onFocus={_handleFocusDatePicker}*/}
              {/*    onBlur={_handleBlurDatePicker}*/}
              {/*  />*/}
              {/*</div>*/}
            </div>
          </div>
        }
      </div>

      <div className="bottomButtonBlock">
        <div className="flex flex-row">
          <Button
            text={btnViewDetail ? t('view_detail') : t('contract_search')}
            extraClassName="w-full"
            onClick={_handleSubmit}
          />
        </div>
      </div>
    </div>
  );
});

const Payment = () => <FeeFormObserver feeStore={feeSt} policyStore={policySt} paymentStore={paymentSt} />;
export default Payment;
